import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Droplets } from 'lucide-react';
import { useHydrationGroups } from '../../../contexts/HydrationGroupsContext';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import { ROUTES, HYDRATION_INTERVALS, VALIDATION } from '../../../config/constants';

/**
 * Form de creación de balde de agua (hydrationGroup).
 *
 * Vive en /water-groups/new. Mismo patrón que GroupForm de comida:
 *  - Standalone (sin createPet inline) para evitar grupos huérfanos.
 *  - Si viene con ?returnTo=<path>, al guardar redirige a `${returnTo}?selectGroup=<id>`
 *    para que el form de origen lo autoseleccione.
 *
 * Campos:
 *  - name (1-40 chars)
 *  - hydrationIntervalHours (chip selector: 6/12/24/48)
 */

const FIELD_ERROR_MAP = {
  INVALID_NAME: 'name',
  INVALID_HYDRATION_INTERVAL: 'hydrationIntervalHours',
};

const WaterGroupForm = () => {
  const { createHydrationGroup } = useHydrationGroups();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const returnTo = params.get('returnTo');

  const [name, setName] = useState('');
  const [intervalHours, setIntervalHours] = useState(12);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!name.trim()) {
      setErrors({ name: 'El nombre es requerido' });
      return;
    }
    if (!HYDRATION_INTERVALS.includes(intervalHours)) {
      setErrors({ hydrationIntervalHours: 'Frecuencia inválida' });
      return;
    }

    try {
      setSubmitting(true);
      const { groupId } = await createHydrationGroup({
        name,
        hydrationIntervalHours: intervalHours,
      });
      toast.success(`Balde "${name.trim()}" creado.`);

      if (returnTo) {
        const sep = returnTo.includes('?') ? '&' : '?';
        navigate(`${returnTo}${sep}selectWaterGroup=${groupId}`, { replace: true });
      } else {
        navigate(ROUTES.INVENTORY, { replace: true });
      }
    } catch (err) {
      const field = FIELD_ERROR_MAP[err.code];
      if (field) {
        setErrors({ [field]: err.message });
      } else {
        toast.error(err.message || 'No se pudo crear el balde.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (returnTo) {
      navigate(returnTo, { replace: true });
    } else {
      navigate(ROUTES.INVENTORY, { replace: true });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-lg bg-sky-100 p-2.5">
            <Droplets className="h-5 w-5 text-sky-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Datos del balde</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Un balde es el recipiente físico donde toman agua una o más mascotas.
              Si comparten balde, tocar "Dar agua" registra a todas a la vez.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Input
            label="Nombre del balde"
            placeholder="Ej: Cocina, Patio, Balde del living..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
            maxLength={VALIDATION.HYDRATION_GROUP_NAME_MAX}
            autoFocus
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              ¿Cada cuánto se cambia / refresca el agua?
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {HYDRATION_INTERVALS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setIntervalHours(h)}
                  className={[
                    'flex-1 min-w-[60px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                    intervalHours === h
                      ? 'border-sky-500 bg-sky-50 text-sky-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {h}h
                </button>
              ))}
            </div>
            {errors.hydrationIntervalHours && (
              <p className="mt-1.5 text-sm text-status-red">
                {errors.hydrationIntervalHours}
              </p>
            )}
            <p className="mt-2 text-xs text-slate-500">
              El semáforo se vuelve amarillo a la mitad del intervalo y rojo cuando
              está vencido.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          fullWidth
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button type="submit" variant="primary" fullWidth isLoading={submitting}>
          Crear balde
        </Button>
      </div>
    </form>
  );
};

export default WaterGroupForm;
